import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  UserGroupIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ArrowRightOnRectangleIcon,
  MoonIcon,
  SunIcon,
  ClipboardDocumentListIcon,
  FaceSmileIcon,
  FingerPrintIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';
import {
  isBiometricAvailable,
  isBiometricEnabled,
  getBiometricType,
  disableBiometricLogin
} from '../../utils/biometricAuth';

const ProfileMenu = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const isAdmin = user?.role === 'Admin';
  const isStaff = user?.role === 'Staff' || isAdmin;
  
  // Biometric state
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricType, setBiometricType] = useState(null);

  // Check biometric status on mount
  useEffect(() => {
    const checkBiometric = async () => {
      if (!window.Capacitor) return;

      const available = await isBiometricAvailable();
      setBiometricAvailable(available);

      if (available) {
        const enabled = await isBiometricEnabled();
        setBiometricEnabled(enabled);

        const type = await getBiometricType();
        setBiometricType(type);
      }
    };

    checkBiometric();
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      onLogout();
    }
  };

  const handleToggleBiometric = async () => {
    if (biometricEnabled) {
      // Disable biometric
      if (window.confirm('Disable biometric login? You will need to enter your password next time.')) {
        await disableBiometricLogin();
        setBiometricEnabled(false);
        alert('Biometric login disabled');
      }
    } else {
      // To enable, user needs to login again with the checkbox
      alert('To enable biometric login, please logout and login again with the "Enable Biometric" checkbox checked.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      {/* User Info Card */}
      <div className="bg-white dark:bg-gray-800 p-6 mb-4">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-white">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {user?.name || 'User'}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {user?.email || ''}
            </p>
            <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
              {user?.role || 'User'}
            </span>
          </div>
        </div>
      </div>

      {/* Admin Section */}
      {isStaff && (
        <div className="bg-white dark:bg-gray-800 mb-4">
          <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Management
            </h3>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {isAdmin && (
              <button
                onClick={() => handleNavigation('/users')}
                className="w-full flex items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <UserGroupIcon className="w-6 h-6 text-gray-600 dark:text-gray-400 mr-4" />
                <div className="flex-1 text-left">
                  <p className="text-base font-medium text-gray-900 dark:text-white">
                    Users
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Manage user accounts
                  </p>
                </div>
              </button>
            )}
            
            {isAdmin && (
              <button
                onClick={() => handleNavigation('/admin/audit-log')}
                className="w-full flex items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <ClipboardDocumentListIcon className="w-6 h-6 text-gray-600 dark:text-gray-400 mr-4" />
                <div className="flex-1 text-left">
                  <p className="text-base font-medium text-gray-900 dark:text-white">
                    Audit Log
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    View system activity
                  </p>
                </div>
              </button>
            )}

            {isAdmin && (
              <button
                onClick={() => handleNavigation('/admin/management-console')}
                className="w-full flex items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <ClipboardDocumentListIcon className="w-6 h-6 text-gray-600 dark:text-gray-400 mr-4" />
                <div className="flex-1 text-left">
                  <p className="text-base font-medium text-gray-900 dark:text-white">
                    Management Console
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Manage sectors and templates
                  </p>
                </div>
              </button>
            )}
            
            <button
              onClick={() => handleNavigation('/reports')}
              className="w-full flex items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ChartBarIcon className="w-6 h-6 text-gray-600 dark:text-gray-400 mr-4" />
              <div className="flex-1 text-left">
                <p className="text-base font-medium text-gray-900 dark:text-white">
                  Reports
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  View analytics and reports
                </p>
              </div>
            </button>

            <button
              onClick={() => handleNavigation('/admin/services')}
              className="w-full flex items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <DocumentTextIcon className="w-6 h-6 text-gray-600 dark:text-gray-400 mr-4" />
              <div className="flex-1 text-left">
                <p className="text-base font-medium text-gray-900 dark:text-white">
                  Service Monitor
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Monitor system services
                </p>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Settings Section */}
      <div className="bg-white dark:bg-gray-800 mb-4">
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Settings
          </h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          <button
            onClick={toggleTheme}
            className="w-full flex items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {theme === 'dark' ? (
              <SunIcon className="w-6 h-6 text-gray-600 dark:text-gray-400 mr-4" />
            ) : (
              <MoonIcon className="w-6 h-6 text-gray-600 dark:text-gray-400 mr-4" />
            )}
            <div className="flex-1 text-left">
              <p className="text-base font-medium text-gray-900 dark:text-white">
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Switch theme
              </p>
            </div>
          </button>

          {/* Biometric Login Toggle (only show if available on device) */}
          {window.Capacitor && biometricAvailable && (
            <button
              onClick={handleToggleBiometric}
              className="w-full flex items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {biometricType === 'faceId' ? (
                <FaceSmileIcon className="w-6 h-6 text-gray-600 dark:text-gray-400 mr-4" />
              ) : (
                <FingerPrintIcon className="w-6 h-6 text-gray-600 dark:text-gray-400 mr-4" />
              )}
              <div className="flex-1 text-left">
                <p className="text-base font-medium text-gray-900 dark:text-white">
                  {biometricType === 'faceId' ? 'Face ID Login' : 'Biometric Login'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {biometricEnabled ? 'Enabled • Tap to disable' : 'Disabled • Tap to learn more'}
                </p>
              </div>
              <div className={`w-12 h-6 rounded-full transition-colors ${biometricEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
                <div className={`w-5 h-5 mt-0.5 rounded-full bg-white transition-transform ${biometricEnabled ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
              </div>
            </button>
          )}
        </div>
      </div>

      {/* App Info */}
      <div className="bg-white dark:bg-gray-800 mb-4 px-6 py-4">
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
          MiBOT Onboarding v1.0.0
        </p>
      </div>

      {/* Logout Button */}
      <div className="px-6">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center px-6 py-4 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
        >
          <ArrowRightOnRectangleIcon className="w-6 h-6 mr-2" />
          <span className="text-base font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
};

export default ProfileMenu;

